// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import styled from '@emotion/styled';
import { bool, object } from 'prop-types';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { Routes, Route, Navigate } from 'react-router-dom';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { Info } from 'lucide-react';
import { SkeletonPlaceholder } from '@carbon/react';

import useNumberUtils from 'Hooks/useNumberUtils';
import { NWTabsNavigation } from 'Components/shared';
import Invoices from 'Containers/Reports/Invoices';
import UnpaidInvoices from 'Containers/Reports/UnpaidInvoices';
import Balance from 'Containers/Reports/Balance';
import Payments from 'Containers/Reports/Payments';
import AccountStatements from 'Containers/Reports/AccountStatements';
import usePermissions from 'Hooks/usePermissions';

const StyledReports = styled.div`
  .totals {
    display: flex;
    padding: 10px 0;

    .totalsItem {
      background-color: #f5f7f9;
      border-color: #cad3de;
      border: 1px solid #cad3de;
      flex: 1;
      padding: 24px 0;
      text-align: center;

      &:first-of-type {
        border-right: none;
      }

      &.danger {
        background-color: #fff2f1;
        border-color: #fbd7d4;

        .totalsItemValue {
          color: #d52b1e;
        }
      }

      .totalsItemLabel {
        display: flex;
        justify-content: center;
        gap: 5px;
        line-height: 2rem;
      }

      .react-tooltip {
        max-width: 300px;
        text-align: left;

        p {
          font-size: 12px;
        }
      }

      .totalsItemValue {
        text-transform: capitalize;
        font-weight: 700;
      }
    }
  }

  @media screen and (max-width: 1366px) {
    .totals {
      flex-direction: column;

      .totalsItem {
        text-align: left;
        padding: 15px;

        .totalsItemLabel {
          justify-content: start;
        }
      }
    }
  }
`;

const StyledSkeletonPlaceholder = styled(SkeletonPlaceholder)`
  height: 97px;
  width: 100%;
`;

const ReportsComponent = ({
  totals,
  totalsLoading,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatNumber } = useNumberUtils();
  const { canAccessAccountStatements } = usePermissions();
  const links = useMemo(
    () => [
      {
        to: 'invoices',
        label: t('profile.menu.invoices')
      },
      {
        to: 'unpaid-invoices',
        label: t('profile.menu.unpaidInvoices')
      },
      {
        to: 'balance',
        label: t('profile.menu.balanceDocuments')
      },
      {
        to: 'payments',
        label: t('profile.menu.payments')
      },
      ...(canAccessAccountStatements ? [{
        to: 'account-statements',
        label: t('profile.menu.accountStatements')
      }] : [])
    ],
    [canAccessAccountStatements, t]
  );

  return (
    <StyledReports theme={ theme }>
      <div className="pageTitle">
        <h3>{ t('common.pages.reports.reports') }</h3>
      </div>
      { totalsLoading ? <StyledSkeletonPlaceholder /> : (
        <>
          { Object.keys(totals).length > 0 && (
            <div className="totals">
              <div className={ `totalsItem ${ totals?.overdueInvoicesCount === 0 ? '' : 'danger' }` }>
                <div className="totalsItemLabel">
                  <span>{ t('reports.overdueInvoicesTotalAmount') }</span>
                  <span>({ totals?.overdueInvoicesCount })</span>
                </div>
                { totals?.overdueInvoicesTotalAmount.length > 0 ? totals?.overdueInvoicesTotalAmount.map(invoicesTotalAmount => (
                  <div key={ invoicesTotalAmount.amount } className="totalsItemValue">
                    { formatNumber(invoicesTotalAmount.amount, { currency: invoicesTotalAmount.currencyId }) }
                  </div>
                )) : (
                  <div className="totalsItemValue">0</div>
                ) }
              </div>
              <div className="totalsItem">
                <div className="totalsItemLabel">
                  <span>{ t('reports.dueInvoicesTotalAmount') }</span>
                  <span>({ totals?.dueInvoicesCount })</span>
                </div>
                { totals?.dueInvoicesTotalAmount.length > 0 ? totals?.dueInvoicesTotalAmount.map(invoicesTotalAmount => (
                  <div key={ invoicesTotalAmount.amount } className="totalsItemValue">
                    { formatNumber(invoicesTotalAmount.amount, { currency: invoicesTotalAmount.currencyId }) }
                  </div>
                )) : (
                  <div className="totalsItemValue">0</div>
                ) }
              </div>
              <div className="totalsItem">
                <div className="totalsItemLabel">
                  <span>{ t('reports.invoicesTotalAmount') }</span>
                  <span id="totalAmount"><Info size={ 18 } /></span>
                  <ReactTooltip
                    className="totalAmount"
                    anchorId="totalAmount"
                    variant="light"
                    offset={ 10 }
                    content={ (
                      <p>{ t('reports.invoicesTotalAmountInfo') }</p>
                    ) }
                  />
                </div>
                { totals?.unpaidInvoicesTotalAmount.length > 0 ? totals?.unpaidInvoicesTotalAmount.map(invoicesTotalAmount => (
                  <div key={ invoicesTotalAmount.amount } className="totalsItemValue">
                    { formatNumber(invoicesTotalAmount.amount, { currency: invoicesTotalAmount.currencyId }) }
                  </div>
                )) : (
                  <div className="totalsItemValue">0</div>
                ) }
              </div>
            </div>
          ) }
        </>
      )}
      <NWTabsNavigation links={ links } />
      <Routes>
        <Route index element={ <Navigate to="invoices" replace /> } />
        <Route path="invoices" element={ <Invoices /> } />
        <Route path="unpaid-invoices" element={ <UnpaidInvoices /> } />
        <Route path="balance" element={ <Balance /> } />
        <Route path="payments" element={ <Payments /> } />
        { canAccessAccountStatements && <Route path="account-statements" element={ <AccountStatements /> } /> }
        <Route path="*" element={ <Navigate to="/not-found" replace /> } />
      </Routes>
    </StyledReports>
  );
};

ReportsComponent.propTypes = {
  totals: object,
  totalsLoading: bool,
};

ReportsComponent.displayName = 'ReportsComponent';

export default ReportsComponent;
